import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import productService from '../services/productService';
import { api } from '../services/authService';

const ReviewsContext = createContext(null);

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

export const ReviewsProvider = ({ children }) => {
  const [reviews, setReviews] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Track when each product's reviews were last fetched
  const lastFetchTime = useRef({});
  // Track in-flight requests to prevent duplicate fetches
  const pendingRequests = useRef({});

  // Получить отзывы для товара (with caching)
  const fetchReviews = useCallback(async (productId, options = {}) => {
    const { page = 1, limit = 10, sort = 'newest', forceRefresh = false } = options;
    const cacheKey = `${productId}-${sort}`;
    
    // Check if we have cached data that's still fresh
    const lastFetch = lastFetchTime.current[cacheKey];
    const cachedData = reviews[productId];
    const isCacheFresh = lastFetch && (Date.now() - lastFetch) < CACHE_DURATION;
    
    // Return cached data if fresh and not forcing refresh and first page
    if (cachedData && isCacheFresh && page === 1 && !forceRefresh) {
      return cachedData;
    }
    
    // Prevent duplicate in-flight requests
    if (pendingRequests.current[cacheKey]) {
      return pendingRequests.current[cacheKey];
    }
    
    setLoading(true);
    setError(null);

    const requestPromise = (async () => {
      try {
        const response = await productService.getReviews(productId, {
          page,
          limit,
          sort,
        });

        // axios возвращает {data: {success, data: {reviews, summary}}}
        const responseData = response.data || response;
        const apiData = responseData.data || responseData; // данные из API
        const rawReviews = apiData.reviews || [];

        // Преобразуем photos из {data, type} объектов в data-uri строки
        // и author из строки в объект
        const reviewsData = rawReviews.map((review) => ({
          ...review,
          // Нормализуем author в объект {name, avatar}
          author:
            typeof review.author === 'string'
              ? { name: review.author, avatar: null }
              : review.author || { name: 'Аноним', avatar: null },
          photos: Array.isArray(review.photos)
            ? review.photos
                .map((photo) => {
                  if (typeof photo === 'string') return photo; // Уже data-uri или URL
                  if (photo && photo.data) {
                    const type = photo.type || 'jpeg';
                    // Проверяем, начинается ли уже с data:
                    if (photo.data.startsWith('data:')) return photo.data;
                    return `data:image/${type};base64,${photo.data}`;
                  }
                  return null;
                })
                .filter(Boolean)
            : [],
        }));

        // Рассчитываем ratingDistribution из отзывов
        const ratingDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        reviewsData.forEach((review) => {
          const rating = review.rating;
          if (rating >= 1 && rating <= 5) {
            ratingDistribution[rating]++;
          }
        });

        const newData = {
          items:
            page === 1
              ? reviewsData
              : [...(reviews[productId]?.items || []), ...reviewsData],
          total:
            apiData.summary?.totalReviews ||
            apiData.total ||
            reviewsData.length,
          averageRating:
            apiData.summary?.averageRating || apiData.averageRating || 0,
          ratingDistribution,
          page,
          hasMore: reviewsData.length === limit,
        };

        setReviews((prev) => ({
          ...prev,
          [productId]: newData,
        }));
        
        // Update cache timestamp
        lastFetchTime.current[cacheKey] = Date.now();

        return newData;
      } catch (err) {
        console.error('[ReviewsContext] Error fetching reviews:', err);
        setError(err.message);
        // Возвращаем mock данные для демо
        const mockData = generateMockReviews(productId);
        setReviews((prev) => ({
          ...prev,
          [productId]: mockData,
        }));
        lastFetchTime.current[cacheKey] = Date.now();
        return mockData;
      } finally {
        setLoading(false);
        delete pendingRequests.current[cacheKey];
      }
    })();
    
    pendingRequests.current[cacheKey] = requestPromise;
    return requestPromise;
  }, [reviews]);

  // Добавить отзыв
  const addReview = useCallback(async (productId, reviewData) => {
    setLoading(true);
    setError(null);

    console.log(
      '[ReviewsContext] addReview called with photos:',
      reviewData.photos?.length || 0
    );

    // Сохраняем фото до любых операций
    const photoUrls = Array.isArray(reviewData.photos)
      ? [...reviewData.photos]
      : [];

    try {
      const response = await productService.addReview(productId, reviewData);

      // Не вызываем fetchReviews - добавляем локально с фото
      const newReview = {
        id: response.data?.id || Date.now().toString(),
        rating: reviewData.rating,
        title: reviewData.title,
        comment: reviewData.comment,
        pros: reviewData.pros,
        cons: reviewData.cons,
        author: {
          name: 'Вы',
          avatar: null,
        },
        photos: photoUrls,
        createdAt: new Date().toISOString(),
        helpful: 0,
        verified: false,
      };

      setReviews((prev) => {
        const current = prev[productId] || {
          items: [],
          total: 0,
          averageRating: 0,
          ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        };
        const newItems = [newReview, ...current.items];
        const newTotal = current.total + 1;
        const newAvg =
          (current.averageRating * current.total + reviewData.rating) /
          newTotal;
        const newDistribution = { ...current.ratingDistribution };
        newDistribution[reviewData.rating] =
          (newDistribution[reviewData.rating] || 0) + 1;

        return {
          ...prev,
          [productId]: {
            ...current,
            items: newItems,
            total: newTotal,
            averageRating: Math.round(newAvg * 10) / 10,
            ratingDistribution: newDistribution,
          },
        };
      });

      return response.data || response;
    } catch (err) {
      console.error('[ReviewsContext] Error adding review:', err);

      // Mock для демо - добавляем отзыв локально
      const mockReview = {
        id: Date.now().toString(),
        rating: reviewData.rating,
        title: reviewData.title,
        comment: reviewData.comment,
        pros: reviewData.pros,
        cons: reviewData.cons,
        author: {
          name: 'Вы',
          avatar: null,
        },
        photos: photoUrls,
        createdAt: new Date().toISOString(),
        helpful: 0,
        verified: false,
      };

      console.log(
        '[ReviewsContext] Created mock review with photos:',
        photoUrls.length,
        photoUrls
      );

      setReviews((prev) => {
        const current = prev[productId] || {
          items: [],
          total: 0,
          averageRating: 0,
          ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        };
        const newItems = [mockReview, ...current.items];
        const newTotal = current.total + 1;
        const newAvg =
          (current.averageRating * current.total + reviewData.rating) /
          newTotal;
        const newDistribution = { ...current.ratingDistribution };
        newDistribution[reviewData.rating] =
          (newDistribution[reviewData.rating] || 0) + 1;

        return {
          ...prev,
          [productId]: {
            ...current,
            items: newItems,
            total: newTotal,
            averageRating: Math.round(newAvg * 10) / 10,
            ratingDistribution: newDistribution,
          },
        };
      });

      return mockReview;
    } finally {
      setLoading(false);
    }
  }, []);

  // Отметить отзыв как полезный
  const markHelpful = useCallback(async (productId, reviewId) => {
    try {
      await api.post(`/api/reviews/${reviewId}/helpful`);

      setReviews((prev) => {
        const current = prev[productId];
        if (!current) return prev;

        return {
          ...prev,
          [productId]: {
            ...current,
            items: current.items.map((r) =>
              r.id === reviewId ? { ...r, helpful: r.helpful + 1 } : r
            ),
          },
        };
      });
    } catch {
      // Mock - увеличиваем локально
      setReviews((prev) => {
        const current = prev[productId];
        if (!current) return prev;

        return {
          ...prev,
          [productId]: {
            ...current,
            items: current.items.map((r) =>
              r.id === reviewId ? { ...r, helpful: r.helpful + 1 } : r
            ),
          },
        };
      });
    }
  }, []);

  // Получить отзывы для товара из кэша
  const getProductReviews = useCallback(
    (productId) => {
      return reviews[productId] || null;
    },
    [reviews]
  );

  const value = {
    reviews,
    loading,
    error,
    fetchReviews,
    addReview,
    markHelpful,
    getProductReviews,
  };

  return (
    <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>
  );
};

export const useReviews = () => {
  const context = useContext(ReviewsContext);
  if (!context) {
    throw new Error('useReviews must be used within ReviewsProvider');
  }
  return context;
};

// Mock данные для демо
function generateMockReviews(productId) {
  const names = [
    'Алексей К.',
    'Мария С.',
    'Дмитрий В.',
    'Анна П.',
    'Сергей М.',
    'Елена Т.',
  ];
  const comments = [
    {
      title: 'Отличное качество!',
      comment:
        'Очень доволен покупкой. Материалы высокого качества, сборка простая. Рекомендую!',
      pros: 'Качество материалов, простая сборка',
      cons: 'Цена выше среднего',
      rating: 5,
    },
    {
      title: 'Хороший товар',
      comment:
        'В целом неплохо, но ожидал большего за эту цену. Выглядит красиво, но есть мелкие недочёты.',
      pros: 'Внешний вид, комплектация',
      cons: 'Мелкие царапины при доставке',
      rating: 4,
    },
    {
      title: 'Соответствует описанию',
      comment:
        'Товар полностью соответствует описанию. Доставка быстрая, упаковка надёжная.',
      pros: 'Быстрая доставка, качественная упаковка',
      cons: 'Не обнаружил',
      rating: 5,
    },
    {
      title: 'Нормально',
      comment:
        'Обычный товар за свои деньги. Ничего выдающегося, но и минусов особых нет.',
      pros: 'Адекватная цена',
      cons: 'Среднее качество фурнитуры',
      rating: 3,
    },
    {
      title: 'Превзошёл ожидания',
      comment:
        'Заказывал с опаской, но товар оказался даже лучше, чем на фото! Очень рад покупке.',
      pros: 'Качество, внешний вид, функциональность',
      cons: 'Нет',
      rating: 5,
    },
  ];

  const mockItems = comments
    .slice(0, 3 + Math.floor(Math.random() * 3))
    .map((c, i) => ({
      id: `mock-${productId}-${i}`,
      ...c,
      author: {
        name: names[i % names.length],
        avatar: null,
      },
      photos:
        i === 0
          ? [
              'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=200',
              'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=200',
            ]
          : [],
      createdAt: new Date(
        Date.now() - i * 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
      helpful: Math.floor(Math.random() * 15),
      verified: Math.random() > 0.3,
    }));

  const totalRating = mockItems.reduce((sum, r) => sum + r.rating, 0);
  const avgRating = mockItems.length > 0 ? totalRating / mockItems.length : 0;

  // Распределение оценок
  const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  mockItems.forEach((r) => distribution[r.rating]++);

  return {
    items: mockItems,
    total: mockItems.length,
    averageRating: Math.round(avgRating * 10) / 10,
    ratingDistribution: distribution,
    page: 1,
    hasMore: false,
  };
}

export default ReviewsContext;
